export type PredictiveActionOptions<TResponse> = {
  // Start the animation; resolves when it completes
  startAnimation: () => Promise<void>;

  // Start the server call
  networkCall: () => Promise<TResponse>;

  // Called once both sides are done and validation passed
  onSuccess?: (response: TResponse) => void;

  // Optional hooks
  onStart?: () => void;

  // Return true if server result matches your prediction, false if not
  onValidate?: (response: TResponse) => boolean | Promise<boolean>;

  // Called after validation (even if animation is still running)
  onResponse?: (response: TResponse) => void;

  // Called if the animation finishes while the server call is still pending — use it to
  // settle the animation and show a loading state for the remaining wait
  onAwaitingResponse?: () => void;

  // Called if network error or validation fails
  onError?: (err: any) => void;

  // Called when we need to roll back the prediction
  rollback?: () => void | Promise<void>;
};

export async function runPredictiveAction<TResponse>(
  options: PredictiveActionOptions<TResponse>
): Promise<TResponse> {
  const {
    startAnimation,
    networkCall,
    onStart,
    onValidate,
    onResponse,
    onAwaitingResponse,
    onError,
    onSuccess,
    rollback,
  } = options;

  let validationPassed = true;
  let responseValue: TResponse | null = null;
  let networkSettled = false;

  const animPromise = startAnimation().then(() => {
    if (!networkSettled && onAwaitingResponse) onAwaitingResponse();
  });

  const networkPromise = (async () => {
    try {
      const resp = await networkCall();
      responseValue = resp;

      let valid = true;
      if (onValidate) valid = await onValidate(resp);
      validationPassed = valid;

      if (onResponse) onResponse(resp);

      if (!valid && rollback) await rollback();
    } catch (err: any) {
      validationPassed = false;
      if (onError) onError(err);
      if (rollback) await rollback();
    } finally {
      networkSettled = true;
    }
  })();

  if (onStart) onStart();

  await Promise.all([animPromise, networkPromise]);

  if (onSuccess && validationPassed && responseValue !== null) {
    onSuccess(responseValue);
  }

  return responseValue as TResponse;
}
